import { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    List, ListItem, ListItemButton, ListItemText, Divider, IconButton, Box, Typography,
    TextField, InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import { INSURERS } from '../utils/insurers';

const MANUAL_PREMIUM = { key: 'manual_premium', label: 'Enter Premium Manually', implemented: true, plans: [] };

export default function AddPlanDialog({ open, onClose, onSubmit, existingPlanIds = [], excludeInsurerKeys = [], onSelectManualPremium, onSelectLifeline, onSelectBwho, onSelectCompany, onSelectOilGas, onSelectBHPIndonesia, onSelectProMedico, onSelectCignaCareConnect, onSelectIhhp, onSelectMorganPrice, onSelectOptimumGlobal, onSelectVumi, onSelectMyHealthPhilippines, onSelectMyHealthSingapore, onSelectMyHealthIndonesia, onSelectCignaGlobalHealthOption, onSelectPacificCrossPrimaryCarePlan, onSelectPacificCrossSelect, onSelectPacificCrossBlueRoyale, onSelectHSBCSmartCareOptimumEnhanced }) {
    const [selectedInsurer, setSelectedInsurer] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (open) {
            setSelectedInsurer(null);
            setSearchQuery('');
        }
    }, [open]);

    function handleSelectInsurer(insurer) {
        if (insurer.key === MANUAL_PREMIUM.key) {
            onClose();
            onSelectManualPremium?.();
            return;
        }
        if (insurer.key === 'lifeline') {
            onClose();
            onSelectLifeline?.();
            return;
        }
        if (insurer.key === 'bwho') {
            onClose();
            onSelectBwho?.();
            return;
        }
        if (insurer.key === 'company') {
            onClose();
            onSelectCompany?.();
            return;
        }
        if (insurer.key === 'oil_gas') {
            onClose();
            onSelectOilGas?.();
            return;
        }
        if (insurer.key === 'bhp_indonesia') {
            onClose();
            onSelectBHPIndonesia?.();
            return;
        }
        if (insurer.key === 'pro_medico') {
            onClose();
            onSelectProMedico?.();
            return;
        }
        if (insurer.key === 'cigna_care_connect') {
            onClose();
            onSelectCignaCareConnect?.();
            return;
        }
        if (insurer.key === 'ihhp') {
            onClose();
            onSelectIhhp?.();
            return;
        }
        if (insurer.key === 'morgan_price') {
            onClose();
            onSelectMorganPrice?.();
            return;
        }
        if (insurer.key === 'optimum_global') {
            onClose();
            onSelectOptimumGlobal?.();
            return;
        }
        if (insurer.key === 'vumi') {
            onClose();
            onSelectVumi?.();
            return;
        }
        if (insurer.key === 'my_health_philippines') {
            onClose();
            onSelectMyHealthPhilippines?.();
            return;
        }
        if (insurer.key === 'my_health_singapore') {
            onClose();
            onSelectMyHealthSingapore?.();
            return;
        }
        if (insurer.key === 'my_health_indonesia') {
            onClose();
            onSelectMyHealthIndonesia?.();
            return;
        }
        if (insurer.key === 'cigna_global_health_option') {
            onClose();
            onSelectCignaGlobalHealthOption?.();
            return;
        }
        if (insurer.key === 'pacific_cross_primary_care_plan') {
            onClose();
            onSelectPacificCrossPrimaryCarePlan?.();
            return;
        }
        if (insurer.key === 'pacific_cross_select') {
            onClose();
            onSelectPacificCrossSelect?.();
            return;
        }
        if (insurer.key === 'pacific_cross_blue_royale') {
            onClose();
            onSelectPacificCrossBlueRoyale?.();
            return;
        }
        if (insurer.key === 'hsbc_smart_care_optimum_enhanced') {
            onClose();
            onSelectHSBCSmartCareOptimumEnhanced?.();
            return;
        }
        setSelectedInsurer(insurer);
        setSearchQuery('');
    }

    function handleSelectPlan(plan) {
        onSubmit({ id: plan.id, label: `${selectedInsurer.label} ${plan.label}` });
        onClose();
    }

    const title = selectedInsurer ? selectedInsurer.label : 'Select Insurer';

    const query = searchQuery.trim().toLowerCase();
    const visibleInsurers = [MANUAL_PREMIUM, ...INSURERS]
        .filter(i => i.implemented && !excludeInsurerKeys.includes(i.key))
        .filter(i => i.label.toLowerCase().includes(query));
    const visiblePlans = selectedInsurer
        ? selectedInsurer.plans.filter(p => p.label.toLowerCase().includes(query))
        : [];

    return (
        <Dialog
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 7,
                    py: 1.5,
                    px: 1.5,
                    backgroundColor: '#f1f4fa',
                    maxHeight: '70vh',
                },
            }}
            open={open}
            onClose={onClose}
            maxWidth="xs"
            
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {selectedInsurer && (
                        <IconButton size="small" onClick={() => { setSelectedInsurer(null); setSearchQuery(''); }} sx={{ ml: -1 }}>
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    )}
                    <Typography variant="h6" component="span">{title}</Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ px: 2, pb: 1, mt: 1 }}>
                    <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        placeholder={selectedInsurer ? 'Search plans' : 'Search insurers'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 5, backgroundColor: 'transparent' } }}
                    />
                </Box>
                {!selectedInsurer ? (
                    <List disablePadding>
                        {visibleInsurers.map((insurer, index) => (
                            <Box key={insurer.key}>
                                {index > 0 && <Divider />}
                                <ListItem disablePadding>
                                    <ListItemButton onClick={() => handleSelectInsurer(insurer)}>
                                        <ListItemText
                                            primary={insurer.label}
                                            primaryTypographyProps={insurer.key === MANUAL_PREMIUM.key ? { fontWeight: 600 } : undefined}
                                        />
                                        <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                                    </ListItemButton>
                                </ListItem>
                            </Box>
                        ))}
                        {visibleInsurers.length === 0 && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', px: 2, py: 1.5 }}>
                                No insurers found
                            </Typography>
                        )}
                    </List>
                ) : (
                    <List disablePadding>
                        {visiblePlans.map((plan, index) => {
                            const alreadyAdded = existingPlanIds.includes(plan.id);
                            return (
                                <Box key={plan.id}>
                                    {index > 0 && <Divider />}
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() => handleSelectPlan(plan)}
                                            disabled={alreadyAdded}
                                        >
                                            <ListItemText
                                                primary={plan.label}
                                                secondary={alreadyAdded ? 'Already added' : null}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                </Box>
                            );
                        })}
                        {visiblePlans.length === 0 && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', px: 2, py: 1.5 }}>
                                No plans found
                            </Typography>
                        )}
                    </List>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
}
